import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { getAllDemandData, getAllDocumentsData, getPendingDemandID } from "@src/api/Registration";
import { authUserRoles, loginCheck } from "@src/api/Auth";
import { fetchMemberProfile } from "@src/api/User";
import useRegistrationStore from "@src/stores/registrationStore";
import useNavigationStore from "@src/stores/navigationStore";
import { AddressEnumType, UserRoleType, DocumentType } from "@src/utilities/types";
import toast from "react-hot-toast";
import { dispatchAddressToRegistrationStore, updateRegistrationStoreFromDemand, checkBeneficiariesDocuments } from "@src/utilities/functions";

export const useLoginMutation = (closeModal: () => void) => {
    const { setField, registration } = useRegistrationStore();
    const { setSection, setBlock } = useNavigationStore();
    const [loading, setLoading] = useState<boolean>(false);

    const dispatchMemberProfile = async (userRole: UserRoleType) => {
        const memberData = await fetchMemberProfile();
        if (!memberData) return;
        setField('person.basic', {
            ...registration.person.basic,
            civility: memberData.civility,
            firstname: memberData.first_name,
            lastname: memberData.last_name,
            birthname: memberData.birth_name || memberData.last_name,
            phone: memberData.phone || memberData.mobile,
            residence_country: memberData.addresses?.postal?.country?.code || '',
        });
        if (userRole === UserRoleType.Premium) {
            setField('person.birth', {
                ...registration.person.birth,
                date: Math.floor(new Date(memberData.birth.date).getTime() / 1000),
                place: {
                    city: memberData.birth.city.name,
                    zipcode: memberData.birth.city.zipcode,
                    country: memberData.birth.country.code,
                },
            });
            if (memberData.addresses && memberData.addresses.postal) {
                const postal = memberData.addresses.postal;
                const [streetNumber, ...streetNameParts] = postal.street?.split(' ') || [];
                dispatchAddressToRegistrationStore(registration.addresses, AddressEnumType.postale, {
                    ...postal,
                    country: postal.country.code,
                    street_number: streetNumber || '',
                    street_name: streetNameParts.join(' '),
                });
            }
            if (memberData.addresses && memberData.addresses.fiscal) {
                const fiscal = memberData.addresses.fiscal;
                const [streetNumber, ...streetNameParts] = fiscal.street?.split(' ') || [];
                dispatchAddressToRegistrationStore(registration.addresses, AddressEnumType.fiscale, {
                    ...fiscal,
                    country: fiscal.country.code,
                    street_number: streetNumber || '',
                    street_name: streetNameParts.join(' '),
                });
            }
        }
    };

    const navigate = (section: number, block: string) => {
        setSection(section);
        setBlock(block);
        closeModal();
    }

    const handleNavigationAfterLogin = async (userRole: UserRoleType, demandData?: any, documentsData?: any) => {
        if (documentsData?.hasFolder) {
            const hasFront = documentsData.documents?.some((doc: { type: string }) => doc.type === DocumentType.front);
            const hasBack = documentsData.documents?.some((doc: { type: string }) => doc.type === DocumentType.back);
            const hasFrontBack = documentsData.documents?.some((doc: { type: string }) => doc.type === DocumentType.frontBack);
            const hasAddress = documentsData.documents?.some((doc: { type: string }) => doc.type === DocumentType.address);

            const hasBeneficiariesMissingDocuments = checkBeneficiariesDocuments(demandData?.company?.beneficial_owners ?? []);
            return navigate(documentsData.signed_at ? 7 : 6,
                documentsData.signed_at
                    ? 'UploadMissingDocs'
                    : documentsData.signable || (hasFront && hasBack && hasAddress && !hasBeneficiariesMissingDocuments) || (hasFrontBack && hasAddress && !hasBeneficiariesMissingDocuments)
                        ? 'CompanyDocuments'
                        : hasBeneficiariesMissingDocuments
                            ? 'BeneficiariesDocuments'
                            : (hasFront && hasBack) || hasFrontBack
                                ? 'ProofOfAddress'
                                : 'Documents'
            )
        }
        if (demandData?.accounts?.length > 0 && demandData?.accounts[0]?.id) {
            return navigate(5, 'AccountInformations');
        }
        if (demandData?.company?.company_id && demandData?.company?.industry_sector) {
            return navigate(4, 'LCBFT');
        }
        if (demandData?.company?.company_id) {
            return navigate(3, 'SirenNumber');
        }
        if (userRole === UserRoleType.Premium && !demandData?.company?.company_id) {
            return navigate(2, 'LegalAgentSituation');
        } 
        return navigate(1, 'Register');
    };

    const { mutate: login, isPending: isLoginPending } = useMutation({
        mutationFn: ({ login, password }: { login: string; password: string }) => loginCheck({ login, password }),
        onSuccess: async () => {
            setLoading(true);
            try {
                const userRole = await authUserRoles();
                if (!userRole) throw new Error('User role undefined');
                if (userRole.name === UserRoleType.Client) {
                    toast.dismiss();
                    toast.error("Cet identifiant est déjà utilisé par un client Bourse Direct");
                    return;
                }
                setField('user_role', userRole.name);

                const pendingDemand = await getPendingDemandID();
                setField('demand_id', pendingDemand?.demand_id);

                const demandData = pendingDemand?.demand_id ? await getAllDemandData(pendingDemand.demand_id) : null;
                const documentsData = pendingDemand?.demand_id ? await getAllDocumentsData() : null;

                demandData ? updateRegistrationStoreFromDemand(demandData, registration, setField) : await dispatchMemberProfile(userRole.name as UserRoleType);
                await handleNavigationAfterLogin(userRole.name as UserRoleType, demandData, documentsData);
            } catch (error) {
                console.error('Erreur au success de login:', error);
            } finally {
                setLoading(false);
            }
        },
        onError: (error: any) => {
            if (error.response?.status === 401) {
                toast.dismiss();
                toast.error("Le couple login / mot de passe est incorrect");
            }
            console.error('Erreur sur login:', error)},
    });

    return { login, isLoginPending, loading };
};
